<template>
  <div v-if="isScientificTheme">
    <div class="select-scientific-theme-wrapper">
      <div class="select-scientific-title">
        {{ $t("agenda.selected_days") }}
      </div>
      <div v-for="(day, index) in allDays" :key="index" class="select-scientific unselectable" :class="classes(day)" @click="selectDayById(day.id)">
        <checkbox :value="isSelected(day)" @input="selectDayById(day.id)" size="22px"></checkbox>
        <div class="select-scientific-label">
          <span class="notranslate" style="margin-right: 4px">{{ day.only_date | date("dddd") }},</span>
          <span class="notranslate">{{ day.only_date | date("D MMMM") }}</span>
        </div>
      </div>
    </div>
    <div class="select-scientific-paginated" :class="{ 'is-scientific': this.isScientificTheme }">
      <scrollable style="width: 100%" :items="allDays" :limit="pageSize" ref="scrollable">
        <template v-slot="{ visibleItems }">
          <div class="days-container" :class="themeClass">
            <button
              v-for="(day, index) in visibleItems"
              :key="index"
              class="button has-text-weight-bold"
              :class="classes(day)"
              @click="selectDayById(day.id)"
            >
              <div>
                <span class="day-button-day notranslate" style="margin-right: 4px">{{ day.only_date | date("ddd") }} &bullet;</span>
                <span class="day-button-date notranslate">{{ day.only_date | date("D MMM") }}</span>
              </div>
            </button>
          </div>
        </template>
      </scrollable>
    </div>
  </div>
  <scrollable :items="allDays" :limit="pageSize" ref="scrollable" v-else>
    <template v-slot="{ visibleItems }">
      <div class="days-container" :class="themeClass">
        <button
          v-for="(day, index) in visibleItems"
          :key="index"
          class="button has-text-weight-bold"
          :class="classes(day)"
          @click="selectDayById(day.id)"
        >
          <div class="is-flex is-flex-direction-row is-flex-wrap">
            <span class="day-button-day notranslate" style="margin-right: 4px">{{ day.only_date | date("ddd") }} •</span>
            <span class="day-button-date notranslate">{{ day.only_date | date("D MMM") }}</span>
          </div>
        </button>
      </div>
    </template>
  </scrollable>
</template>

<script>
import { mapGetters, mapMutations, mapState } from "vuex";
import dayjs from '@/shared/utils/day';
import Constants from "@/widgets/shared/constants";
import Checkbox from "@/shared/components/form/Checkbox";
import Scrollable from "@/shared/components/common/Scrollable";

export default {
  name: "DaySelect",

  components: {
    Scrollable,
    Checkbox,
  },

  props: ["isEnabled"],

  methods: {
    ...mapMutations("days", ["selectById"]),
    ...mapMutations("paths", { clearPaths: "selectByIndex" }),

    classes(day) {
      return {
        "is-active": this.isSelected(day),
      };
    },

    isSelected(day) {
      return this.activeDay && this.activeDay.id === day.id;
    },

    selectDayById(dayId) {
      this.selectById(dayId);
      this.$emit("day-selected");
    },
  },

  filters: {
    date(value, format) {
      return dayjs(value).format(format);
    },
  },

  computed: {
    ...mapGetters("days", ["allDays", "activeDay"]),
    ...mapState(["theme"]),

    pageSize() {
      return 4;
    },

    columnClass() {
      if (this.allDays.length === 1) {
        return "is-full";
      } else if (this.allDays.length === 2) {
        return "is-half";
      } else if (this.allDays.length === 3) {
        return "is-one-third";
      } else {
        return "is-one-quarter";
      }
    },

    themeClass() {
      return {
        "is-scientific": this.isScientificTheme,
      };
    },

    isScientificTheme() {
      return this.theme === Constants.WIDGET_THEME_TYPES.SCIENTIFIC;
    },
  },

  watch: {
    allDays: {
      immediate: true,
      handler: function (newValue) {
        this.$nextTick(() => {
          if (newValue && newValue.length) {
            const dayIndex = this.allDays.findIndex(day => day.id === this.activeDay.id);
            if (this.$refs.scrollable) {
              this.$refs.scrollable.setIndex(dayIndex);
            }
          }
        });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.days-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 6px;

  &.is-scientific {
    @media not screen and (max-width: 920px), print {
      flex-direction: column;
      align-items: start;
    }
  }
}

.button {
  flex-grow: 1;
  color: var(--text-color-secondary);
  text-transform: capitalize;
  background-color: var(--agenda-day-select-background-color);
  border-color: var(--agenda-day-select-border-color);
  font-size: 15px;

  &.is-active {
    background-color: var(--agenda-day-select-active-background);
    border-color: var(--dropdown-border-color);
  }

  .day-button-day {
    color: var(--agenda-day-select-day-text-color);
  }

  .day-button-date {
    color: var(--agenda-day-select-date-text-color);
  }
}

@media screen and (max-width: 920px), print {
  .button {
    font-size: 12px;
  }
}

// Reverse text color on button active status
// Allows properly coloring the button
.button:active,
.button.is-active {
  .day-button-day {
    color: var(--agenda-day-select-active-text) !important;
  }

  .day-button-date {
    color: var(--agenda-day-select-active-text) !important;
  }
}
</style>
